import { ReactNode } from "react";

export type DeviceType = "desktop" | "phone" | "tablet";

interface PhoneFrameProps {
  children: ReactNode;
  format?: string | null;
  deviceType?: DeviceType;
}

export function PhoneFrame({
  children,
  format,
  deviceType = "desktop",
}: PhoneFrameProps) {
  const isVideoMode = format === "video";

  // Apply transform in device preview mode to create a containing block for fixed-position descendants
  // This makes fixed elements (like PinnedBookmarkButton) position relative to the device frame instead of viewport
  // Also set as container for container queries so elements can respond to device frame size
  const screenClassName = isVideoMode
    ? "@container relative w-full h-[100dvh] xl:h-screen bg-wrapped-dark overflow-hidden"
    : deviceType === "phone"
      ? "@container relative w-full h-full xl:w-[375px] xl:h-[812px] bg-wrapped-dark xl:rounded-[2.5rem] overflow-hidden xl:[transform:translate3d(0,0,0)]"
      : deviceType === "tablet"
        ? "@container relative w-full h-full xl:w-[1024px] xl:h-[768px] bg-wrapped-dark xl:rounded-[2.5rem] overflow-hidden xl:[transform:translate3d(0,0,0)]"
        : "@container relative w-full h-[100dvh] xl:h-screen bg-wrapped-dark overflow-hidden";

  const bezelClassName = isVideoMode
    ? "w-full h-full"
    : deviceType === "desktop"
      ? "w-full h-full"
      : "relative w-full h-full xl:w-auto xl:h-auto xl:bg-black xl:rounded-[3rem] xl:p-3 xl:shadow-2xl";

  return (
    <div className="h-[100dvh] w-full bg-gray-900 xl:min-h-screen xl:h-auto xl:flex xl:items-center xl:justify-center overflow-hidden">
      <div className={bezelClassName}>
        {/* Notch - only show for phone */}
        {deviceType === "phone" && (
          <div className="hidden xl:block absolute top-0 left-1/2 -translate-x-1/2 w-32 h-7 bg-black rounded-b-2xl z-20" />
        )}

        {/* Screen */}
        <div className={screenClassName}>
          {/* Status bar area - only for devices */}
          {deviceType !== "desktop" && (
            <div className="hidden xl:block absolute top-0 left-0 right-0 h-12 z-10 pointer-events-none" />
          )}

          {/* Content - force mobile text scale in phone/tablet preview mode */}
          <div
            className={`h-full w-full ${deviceType !== "desktop" ? "mobile-text-scale" : ""}`}
          >
            {children}
          </div>

          {/* Home indicator - only show for phone */}
          {deviceType === "phone" && (
            <div className="hidden xl:block absolute bottom-2 left-1/2 -translate-x-1/2 w-32 h-1 bg-white/20 rounded-full" />
          )}
        </div>
      </div>
    </div>
  );
}
